import React, { useRef, useState, useEffect, useCallback } from 'react'
import { Play, Pause } from 'lucide-react'

import { prepCanvas } from './canvasUtil'

interface Props { title?: string; description?: string; onComplete?: () => void }

// Giao thoa sóng nước 2 nguồn cùng pha (Lớp 11 — Bài 8): nhìn từ trên.
// u = sin(k·r₁ − ωt) + sin(k·r₂ − ωt). Cực đại khi |r₁−r₂| = m·λ; cực tiểu khi = (m+½)·λ.
const W = 560, H = 320, CELL = 4, SX = 90
const WOMEGA = 4

const WaveInterferenceSimulation: React.FC<Props> = ({
  title = 'Giao thoa sóng hai nguồn',
  description = 'Hai nguồn S₁, S₂ cùng pha tạo vân giao thoa: cực đại (sóng tăng cường) khi hiệu đường đi |r₁−r₂| = m·λ; cực tiểu khi = (m+½)·λ.',
  onComplete,
}) => {
  const cv = useRef<HTMLCanvasElement>(null)
  const raf = useRef<number | null>(null)
  const tRef = useRef(0)
  const [dist, setDist] = useState(120)
  const [lambda, setLambda] = useState(44)
  const [running, setRunning] = useState(true)
  const dRef = useRef(dist), lRef = useRef(lambda), runRef = useRef(running)
  const firstRef = useRef(false)
  useEffect(() => { dRef.current = dist }, [dist])
  useEffect(() => { lRef.current = lambda }, [lambda])
  useEffect(() => { runRef.current = running }, [running])
  const touch = () => { if (!firstRef.current) { firstRef.current = true; onComplete?.() } }

  const draw = useCallback(() => {
    const ctx = prepCanvas(cv.current, W, H); if (!ctx) return
    const d = dRef.current, lam = lRef.current, t = tRef.current
    const k = (2 * Math.PI) / lam
    const cy = H / 2, s1y = cy - d / 2, s2y = cy + d / 2
    for (let gy = 0; gy < H; gy += CELL) for (let gx = 0; gx < W; gx += CELL) {
      const r1 = Math.hypot(gx - SX, gy - s1y), r2 = Math.hypot(gx - SX, gy - s2y)
      const u = Math.sin(k * r1 - WOMEGA * t) + Math.sin(k * r2 - WOMEGA * t)
      const f = (u / 2 + 1) / 2 // 0..1
      const r = Math.round(15 + 175 * f), g = Math.round(40 + 185 * f), b = Math.round(90 + 165 * f)
      ctx.fillStyle = `rgb(${r},${g},${b})`; ctx.fillRect(gx, gy, CELL, CELL)
    }
    // đường trung trực (cực đại trung tâm)
    ctx.strokeStyle = 'rgba(255,255,255,0.45)'; ctx.lineWidth = 1; ctx.setLineDash([5, 4])
    ctx.beginPath(); ctx.moveTo(SX, cy); ctx.lineTo(W, cy); ctx.stroke(); ctx.setLineDash([])
    // 2 nguồn
    for (const [sy, lbl] of [[s1y, 'S₁'], [s2y, 'S₂']] as [number, string][]) {
      ctx.fillStyle = '#dc2626'; ctx.beginPath(); ctx.arc(SX, sy, 6, 0, Math.PI * 2); ctx.fill()
      ctx.strokeStyle = '#fff'; ctx.lineWidth = 2; ctx.stroke()
      ctx.fillStyle = '#fff'; ctx.font = 'bold 13px sans-serif'; ctx.textAlign = 'right'; ctx.fillText(lbl, SX - 10, sy + 4); ctx.textAlign = 'left'
    }
    ctx.fillStyle = 'rgba(255,255,255,0.9)'; ctx.font = 'bold 12px sans-serif'
    ctx.fillText(`d = ${d}   ·   λ = ${lam}`, 12, 20)
  }, [])

  const tick = useCallback(() => {
    if (runRef.current) tRef.current += 0.05
    draw(); raf.current = requestAnimationFrame(tick)
  }, [draw])
  useEffect(() => { raf.current = requestAnimationFrame(tick); return () => { if (raf.current) cancelAnimationFrame(raf.current) } }, [tick])

  return (
    <div className="rounded-2xl border border-blue-200 bg-white p-4 my-3">
      <h4 className="font-bold text-blue-800">{title}</h4>
      <p className="text-sm text-gray-500 mb-3">{description}</p>
      <canvas ref={cv} width={W} height={H} className="w-full rounded-lg border border-gray-100" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2 mt-3 text-sm">
        <label className="flex items-center gap-2"><span className="w-36 text-gray-600">Khoảng cách 2 nguồn d</span><input type="range" min={50} max={220} step={5} value={dist} onChange={(e) => { touch(); setDist(Number(e.target.value)) }} className="flex-1 accent-blue-500" /><span className="w-10 text-right font-semibold">{dist}</span></label>
        <label className="flex items-center gap-2"><span className="w-36 text-gray-600">Bước sóng λ</span><input type="range" min={24} max={72} step={2} value={lambda} onChange={(e) => { touch(); setLambda(Number(e.target.value)) }} className="flex-1 accent-blue-500" /><span className="w-10 text-right font-semibold">{lambda}</span></label>
      </div>
      <div className="flex gap-2 mt-3">
        <button onClick={() => { touch(); setRunning((r) => !r) }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-blue-600 text-white font-bold hover:bg-blue-700">{running ? <><Pause className="w-4 h-4" /> Dừng</> : <><Play className="w-4 h-4" /> Chạy</>}</button>
      </div>
      <p className="text-[11px] text-gray-400 mt-2">Vùng sáng = cực đại giao thoa (2 sóng cùng pha tăng cường); vùng tối xen kẽ = cực tiểu (ngược pha triệt tiêu). Tăng λ hoặc giảm d → vân thưa hơn.</p>
    </div>
  )
}
export default WaveInterferenceSimulation
